/**
 * Add Dependency Modal
 *
 * Always shows two explicit pickers (source + target) regardless of whether
 * opened from an item detail page or from the Dependencies relationship page.
 *
 * When sourceItemId is provided (item-detail context) the source picker is
 * pre-populated with the current item. The user can swap roles so the current
 * item can be either the depender or the dependency target.
 */

import { useState, useEffect } from 'react';
import { X, GitBranch, AlertCircle } from 'lucide-react';
import { useItemRelationshipsStore } from '../../../stores/prioritization/itemRelationshipsStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { SearchableItemPicker } from '../SearchableItemPicker';
import { DEFAULT_SEQUENCE_STRENGTH, SEQUENCE_STRENGTH_OPTIONS } from '../../../lib/sequenceRelationships';
import type { DependencySubtype } from '../../../types/prioritization/itemRelationships';

interface AddDependencyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (edgeId: string) => void;
  /** Pre-populate the source picker when opened from an item detail page. */
  sourceItemId?: string;
}

// The strength words are shared with the prerequisite and sequence-link modals
// (canon 12 §5.4: one set of strengths, whichever end asserts the edge). Gate is
// set from the sequence-link modal and imports, as before.
const STRENGTHS = SEQUENCE_STRENGTH_OPTIONS.filter((o) => o.value !== 'gate');

export function AddDependencyModal({
  isOpen,
  onClose,
  onCreated,
  sourceItemId,
}: AddDependencyModalProps) {
  const createEdge = useItemRelationshipsStore((s) => s.createEdge);
  const fetchFromSupabase = useItemRelationshipsStore((s) => s.fetchFromSupabase);
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);

  // sourceId = the item that HAS the dependency (depends on the target)
  // targetId = the item that IS depended upon
  const [sourceId, setSourceId] = useState<string>(sourceItemId ?? '');
  const [targetId, setTargetId] = useState<string>('');
  const [subtype, setSubtype] = useState<DependencySubtype>(DEFAULT_SEQUENCE_STRENGTH);
  const [label, setLabel] = useState('');
  const [rationale, setRationale] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      void fetchItems();
      void fetchFromSupabase();
    }
  }, [isOpen, fetchItems, fetchFromSupabase]);

  useEffect(() => {
    if (!isOpen) return;
    setSourceId(sourceItemId ?? '');
    setTargetId('');
    setSubtype(DEFAULT_SEQUENCE_STRENGTH);
    setLabel('');
    setRationale('');
    setError(null);
  }, [isOpen, sourceItemId]);

  const handleCreate = async () => {
    setError(null);
    if (!sourceId) {
      setError('Select the item that has the dependency');
      return;
    }
    if (!targetId) {
      setError('Select the item being depended on');
      return;
    }
    if (sourceId === targetId) {
      setError('An item cannot depend on itself');
      return;
    }

    setIsCreating(true);
    try {
      const edge = await createEdge({
        type: 'dependency',
        subtype,
        directionality: 'directed',
        status: 'active',
        declaredMode: 'declared',
        sourceItemId: sourceId,
        targetItemId: targetId,
        label: label.trim() || undefined,
        rationale: rationale.trim() || undefined,
      });
      if (edge) {
        onCreated?.(edge.id);
        onClose();
      } else {
        setError('Failed to create dependency');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create dependency');
    } finally {
      setIsCreating(false);
    }
  };

  if (!isOpen) return null;

  const canSubmit = !!sourceId && !!targetId && sourceId !== targetId && !isCreating;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--bg-raised)] shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <GitBranch className="h-6 w-6 text-[var(--text-ter)]" />
            <div>
              <h2 className="text-xl font-semibold text-[var(--text-pri)]">Add Dependency</h2>
              <p className="text-sm text-[var(--text-ter)]">Sequence or condition relationship (horizontal)</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[var(--hover-tint)]">
            <X className="h-5 w-5 text-[var(--text-ter)]" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-[var(--coral-dim)] border border-[var(--coral)] text-[var(--coral)] text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Source + target pickers */}
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1.5">
                Depends on <span className="text-[var(--text-ter)] font-normal">(item with the dependency)</span>
              </label>
              <SearchableItemPicker
                value={sourceId || undefined}
                onChange={(id) => setSourceId(id ?? '')}
                excludeId={targetId || undefined}
                label=""
                placeholder="Search for the depending item..."
                lifecycleStates={[]}
                allowEmpty={false}
                allowCreate
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1.5">
                Required item <span className="text-[var(--text-ter)] font-normal">(what it depends on)</span>
              </label>
              <SearchableItemPicker
                value={targetId || undefined}
                onChange={(id) => setTargetId(id ?? '')}
                excludeId={sourceId || undefined}
                label=""
                placeholder="Search for the required item..."
                lifecycleStates={[]}
                allowEmpty={false}
                allowCreate
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Strength</label>
            <div className="space-y-2">
              {STRENGTHS.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setSubtype(t.value)}
                  className={`w-full p-3  border-2 text-left ${
                    subtype === t.value ? 'border-[var(--slate)] bg-[var(--slate-dim)]' : 'border-[var(--rule)] hover:border-[var(--rule)]'
                  }`}
                >
                  <div className="font-medium text-sm text-[var(--text-pri)]">{t.label}</div>
                  <div className="text-xs text-[var(--text-ter)] mt-0.5">{t.description}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Label (optional)</label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Blocked by"
              className="w-full px-4 py-2 border border-[var(--rule)]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Rationale (optional)</label>
            <textarea
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              placeholder="Why does this dependency exist?"
              rows={2}
              className="w-full px-4 py-2 border border-[var(--rule)] resize-none"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-[var(--surface)]">
          <button onClick={onClose} className="px-4 py-2 text-[var(--text-sec)] font-medium hover:bg-[var(--hover-tint)]">
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={!canSubmit}
            className="px-4 py-2 bg-[var(--slate)] text-white font-medium hover:bg-[var(--slate)] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isCreating ? 'Adding...' : 'Add dependency'}
          </button>
        </div>
      </div>
    </div>
  );
}
